/**
 * Ticket Detail Page
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/features/auth/auth-context';
import { useToast } from '@/hooks/use-toast';
import { formatDateTime, cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Send,
  Clock,
  AlertTriangle,
  Eye,
  EyeOff,
  Link2,
  Merge,
  Star,
  User,
  MessageSquare,
  Pause,
  Plus,
  Trash2,
  Paperclip,
} from 'lucide-react';

interface TicketDetail {
  id: string;
  ticketNumber: number;
  subject: string;
  status: string;
  priority: string;
  channel: string;
  assigneeId: string | null;
  assigneeName: string | null;
  accountName: string | null;
  applicationName: string | null;
  slaPolicyName: string | null;
  slaResponseDue: string | null;
  slaResolutionDue: string | null;
  slaPausedAt: string | null;
  slaBreach: boolean;
  csatRating: number | null;
  csatComment: string | null;
  createdAt: string;
  updatedAt: string;
}

interface MessageAttachment {
  name: string;
  contentType: string;
  size: number;
}

interface TicketMessage {
  id: string;
  ticketId: string;
  content: string;
  isInternal: boolean;
  senderType: string;
  senderName: string;
  attachments?: MessageAttachment[];
  createdAt: string;
}

interface TicketWatcher {
  id: string;
  userId: string;
  userName: string;
}

interface TicketLink {
  id: string;
  linkType: string;
  linkedTicketId: string;
  linkedTicketNumber: number;
  linkedTicketSubject: string;
}

interface CannedResponse {
  id: string;
  title: string;
  content: string;
  category: string | null;
}

const statusColors: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  waiting_customer: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const priorityColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600',
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_customer: 'Waiting on Customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

const channelLabels: Record<string, string> = {
  widget: 'Widget',
  email: 'Email',
  portal: 'Portal',
  phone: 'Phone',
};

const channelColors: Record<string, string> = {
  widget: 'bg-indigo-100 text-indigo-700',
  email: 'bg-sky-100 text-sky-700',
  portal: 'bg-emerald-100 text-emerald-700',
  phone: 'bg-amber-100 text-amber-700',
};

const linkTypeLabels: Record<string, string> = {
  related: 'Related',
  blocks: 'Blocks',
  blocked_by: 'Blocked By',
  duplicate: 'Duplicate',
  parent: 'Parent',
  child: 'Child',
};

function getSlaTimeRemaining(dueDate: string | null): { text: string; color: string } | null {
  if (!dueDate) return null;
  const now = new Date().getTime();
  const due = new Date(dueDate).getTime();
  const diff = due - now;

  if (diff <= 0) {
    return { text: 'Overdue', color: 'text-red-600' };
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  const totalMinutes = diff / (1000 * 60);
  const originalTotal = (due - now + diff) / (1000 * 60);
  const percentRemaining = originalTotal > 0 ? (totalMinutes / originalTotal) * 100 : 0;

  let color = 'text-green-600';
  if (percentRemaining < 25) color = 'text-red-600';
  else if (percentRemaining < 50) color = 'text-yellow-600';

  if (hours > 0) {
    return { text: `${hours}h ${minutes}m`, color };
  }
  return { text: `${minutes}m`, color };
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  const [replyContent, setReplyContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [linkTicketNumber, setLinkTicketNumber] = useState('');
  const [linkType, setLinkType] = useState('related');
  const [mergeTargetTicketId, setMergeTargetTicketId] = useState('');

  // Fetch ticket
  const { data: ticketData, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: async () => {
      const result = await api.getTicket(id!);
      return result;
    },
    enabled: !!id,
  });

  // Fetch messages
  const { data: messagesData } = useQuery({
    queryKey: ['ticket-messages', id],
    queryFn: async () => {
      const result = await api.getTicketMessages(id!);
      return result;
    },
    enabled: !!id,
  });

  // Authenticated attachment download: fetch as blob, then trigger a save.
  const handleDownloadAttachment = async (messageId: string, index: number, name: string) => {
    try {
      const blob = await api.downloadTicketAttachment(id!, messageId, index);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Download failed', description: 'Could not download the attachment.', variant: 'destructive' });
    }
  };

  // Fetch watchers
  const { data: watchersData } = useQuery({
    queryKey: ['ticket-watchers', id],
    queryFn: async () => {
      const result = await api.getTicketWatchers(id!);
      return result;
    },
    enabled: !!id,
  });

  // Fetch links
  const { data: linksData } = useQuery({
    queryKey: ['ticket-links', id],
    queryFn: async () => {
      const result = await api.getTicketLinks(id!);
      return result;
    },
    enabled: !!id,
  });

  // Fetch canned responses
  const { data: cannedData } = useQuery({
    queryKey: ['canned-responses'],
    queryFn: async () => {
      const result = await api.getCannedResponses({ limit: 100 });
      return result;
    },
  });

  const ticket = ticketData?.data as TicketDetail | undefined;
  const messages = (messagesData?.data || []) as TicketMessage[];
  const watchers = (watchersData?.data || []) as TicketWatcher[];
  const links = (linksData?.data || []) as TicketLink[];
  const cannedResponses = (cannedData?.data || []) as CannedResponse[];

  // Mutations
  const sendMessageMutation = useMutation({
    mutationFn: ({ content, internal, files }: { content: string; internal: boolean; files: File[] }) =>
      files.length > 0
        ? api.addTicketMessageWithFiles(id!, content, internal, files)
        : api.addTicketMessage(id!, content, internal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      setReplyContent('');
      setIsInternal(false);
      setReplyFiles([]);
      toast({ title: isInternal ? 'Internal note added' : 'Reply sent' });
    },
    onError: () => {
      toast({ title: 'Failed to send message', variant: 'destructive' });
    },
  });

  const updateTicketMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.updateTicket(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast({ title: 'Ticket updated' });
    },
    onError: () => {
      toast({ title: 'Failed to update ticket', variant: 'destructive' });
    },
  });

  const addWatcherMutation = useMutation({
    mutationFn: (userId: string) => api.addTicketWatcher(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-watchers', id] });
      toast({ title: 'Added as watcher' });
    },
  });

  const removeWatcherMutation = useMutation({
    mutationFn: (userId: string) => api.removeTicketWatcher(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-watchers', id] });
      toast({ title: 'Removed from watchers' });
    },
  });

  const createLinkMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createTicketLink(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-links', id] });
      setLinkDialogOpen(false);
      setLinkTicketNumber('');
      setLinkType('related');
      toast({ title: 'Ticket linked' });
    },
    onError: () => {
      toast({ title: 'Failed to link ticket', variant: 'destructive' });
    },
  });

  const deleteLinkMutation = useMutation({
    mutationFn: (linkId: string) => api.deleteTicketLink(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-links', id] });
      toast({ title: 'Link removed' });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: (targetTicketId: string) => api.mergeTickets(id!, { targetTicketId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      setMergeDialogOpen(false);
      setMergeTargetTicketId('');
      toast({ title: 'Tickets merged successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to merge tickets', variant: 'destructive' });
    },
  });

  function handleSendReply(e: React.FormEvent) {
    e.preventDefault();
    if (!replyContent.trim() && replyFiles.length === 0) return;
    sendMessageMutation.mutate({ content: replyContent, internal: isInternal, files: replyFiles });
  }

  function handleCannedResponseSelect(responseId: string) {
    const response = cannedResponses.find((r) => r.id === responseId);
    if (response) {
      setReplyContent(response.content);
    }
  }

  function handleAddLink(e: React.FormEvent) {
    e.preventDefault();
    if (!linkTicketNumber.trim()) return;
    createLinkMutation.mutate({
      linkedTicketNumber: parseInt(linkTicketNumber),
      linkType,
    });
  }

  const isCurrentUserWatching = watchers.some((w) => w.userId === user?.id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-medium mb-2">Ticket not found</h2>
        <Button variant="outline" onClick={() => navigate('/support/tickets')}>
          Back to Tickets
        </Button>
      </div>
    );
  }

  const responseDue = getSlaTimeRemaining(ticket.slaResponseDue);
  const resolutionDue = getSlaTimeRemaining(ticket.slaResolutionDue);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/support/tickets')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold truncate">
              #{ticket.ticketNumber} - {ticket.subject}
            </h1>
            <span
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                statusColors[ticket.status]
              )}
            >
              {statusLabels[ticket.status] || ticket.status}
            </span>
            <span
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                priorityColors[ticket.priority]
              )}
            >
              {ticket.priority}
            </span>
          </div>
          <p className="text-muted-foreground text-sm">
            {ticket.accountName || 'No account'} • Created {formatDateTime(ticket.createdAt)}
          </p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column - main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* SLA Section */}
          {ticket.slaPolicyName && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">SLA: {ticket.slaPolicyName}</span>
                  {ticket.slaPausedAt && (
                    <Badge variant="secondary" className="text-xs">
                      <Pause className="h-3 w-3 mr-1" />
                      Paused
                    </Badge>
                  )}
                  {ticket.slaBreach && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      SLA Breached
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <span className="text-sm text-muted-foreground">Response Due</span>
                    {responseDue ? (
                      <span className={cn('text-sm font-medium', responseDue.color)}>
                        {responseDue.text}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">N/A</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <span className="text-sm text-muted-foreground">Resolution Due</span>
                    {resolutionDue ? (
                      <span className={cn('text-sm font-medium', resolutionDue.color)}>
                        {resolutionDue.text}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">N/A</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Message Thread */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Messages ({messages.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {messages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No messages yet.
                </p>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      'rounded-lg border p-4',
                      message.isInternal && 'bg-yellow-50 border-yellow-200'
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                          {message.senderName?.charAt(0) || '?'}
                        </div>
                        <span className="font-medium text-sm">{message.senderName}</span>
                        <span
                          className={cn(
                            'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                            message.senderType === 'agent'
                              ? 'bg-blue-100 text-blue-700'
                              : message.senderType === 'customer'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          )}
                        >
                          {message.senderType}
                        </span>
                        {message.isInternal && (
                          <Badge variant="outline" className="text-xs text-yellow-700 border-yellow-400">
                            <EyeOff className="h-3 w-3 mr-1" />
                            Internal
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(message.createdAt)}
                      </span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap pl-9">{message.content}</div>
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="pl-9 mt-2 flex flex-wrap gap-2">
                        {message.attachments.map((att, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => handleDownloadAttachment(message.id, i, att.name)}
                            className="inline-flex items-center gap-1 rounded border bg-muted/50 px-2 py-1 text-xs hover:bg-muted"
                            title={`${att.contentType} · ${Math.round(att.size / 1024)} KB`}
                          >
                            <Paperclip className="h-3 w-3" />
                            {att.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}

              {/* Reply Form */}
              <form onSubmit={handleSendReply} className="space-y-3 pt-4 border-t">
                <div className="flex items-center gap-4">
                  <Label className="text-sm font-medium">Canned Response</Label>
                  <Select onValueChange={handleCannedResponseSelect}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Select a canned response..." />
                    </SelectTrigger>
                    <SelectContent>
                      {cannedResponses.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder={isInternal ? 'Write an internal note...' : 'Write a reply...'}
                  rows={4}
                />

                {replyFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {replyFiles.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded border bg-muted/50 px-2 py-1 text-xs">
                        <Paperclip className="h-3 w-3" />
                        {f.name}
                        <button
                          type="button"
                          onClick={() => setReplyFiles((prev) => prev.filter((_, idx) => idx !== i))}
                          className="ml-1 text-muted-foreground hover:text-foreground"
                          aria-label={`Remove ${f.name}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isInternal"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="isInternal" className="mb-0 text-sm">
                        Internal note
                      </Label>
                    </div>
                    <Label
                      htmlFor="reply-files"
                      className="mb-0 inline-flex items-center gap-1 text-sm text-muted-foreground cursor-pointer hover:text-foreground"
                    >
                      <Paperclip className="h-4 w-4" />
                      Attach
                    </Label>
                    <input
                      id="reply-files"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        setReplyFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={(!replyContent.trim() && replyFiles.length === 0) || sendMessageMutation.isPending}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {sendMessageMutation.isPending
                      ? 'Sending...'
                      : isInternal
                      ? 'Add Internal Note'
                      : 'Send Reply'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right column - sidebar */}
        <div className="space-y-4">
          {/* Status & Priority */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Ticket Properties</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select
                  value={ticket.status}
                  onValueChange={(value) => updateTicketMutation.mutate({ status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_customer">Waiting on Customer</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Priority</Label>
                <Select
                  value={ticket.priority}
                  onValueChange={(value) => updateTicketMutation.mutate({ priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Assignee</Label>
                <p className="text-sm font-medium flex items-center gap-2 mt-1">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {ticket.assigneeName || 'Unassigned'}
                </p>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Application</Label>
                <p className="text-sm mt-1">{ticket.applicationName || 'N/A'}</p>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Channel</Label>
                <div className="mt-1">
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                      channelColors[ticket.channel] || 'bg-gray-100 text-gray-700'
                    )}
                  >
                    {channelLabels[ticket.channel] || ticket.channel}
                  </span>
                </div>
              </div>

              {ticket.slaPolicyName && (
                <div>
                  <Label className="text-xs text-muted-foreground">SLA Policy</Label>
                  <p className="text-sm mt-1">{ticket.slaPolicyName}</p>
                </div>
              )}

              <div className="pt-3 border-t space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{formatDateTime(ticket.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated</span>
                  <span>{formatDateTime(ticket.updatedAt)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CSAT Rating */}
          {ticket.csatRating !== null && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Customer Satisfaction</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={cn(
                        'h-5 w-5',
                        star <= (ticket.csatRating || 0)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      )}
                    />
                  ))}
                  <span className="text-sm font-medium ml-2">{ticket.csatRating}/5</span>
                </div>
                {ticket.csatComment && (
                  <p className="text-sm text-muted-foreground italic">
                    &ldquo;{ticket.csatComment}&rdquo;
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Watchers */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">
                <Eye className="h-4 w-4 inline mr-1" />
                Watchers ({watchers.length})
              </CardTitle>
              {user && !isCurrentUserWatching ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addWatcherMutation.mutate(user.id)}
                  disabled={addWatcherMutation.isPending}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Watch
                </Button>
              ) : user && isCurrentUserWatching ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeWatcherMutation.mutate(user.id)}
                  disabled={removeWatcherMutation.isPending}
                  className="text-muted-foreground"
                >
                  Unwatch
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {watchers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No watchers</p>
              ) : (
                <div className="space-y-2">
                  {watchers.map((watcher) => (
                    <div key={watcher.id} className="flex items-center gap-2 text-sm">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs">
                        {watcher.userName?.charAt(0) || '?'}
                      </div>
                      <span>{watcher.userName}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked Tickets */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">
                <Link2 className="h-4 w-4 inline mr-1" />
                Linked Tickets ({links.length})
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLinkDialogOpen(true)}
              >
                <Plus className="h-3 w-3 mr-1" />
                Link
              </Button>
            </CardHeader>
            <CardContent>
              {links.length === 0 ? (
                <p className="text-sm text-muted-foreground">No linked tickets</p>
              ) : (
                <div className="space-y-2">
                  {links.map((link) => (
                    <div
                      key={link.id}
                      className="flex items-center justify-between text-sm p-2 rounded border"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge variant="outline" className="text-xs shrink-0">
                          {linkTypeLabels[link.linkType] || link.linkType}
                        </Badge>
                        <span className="truncate">
                          #{link.linkedTicketNumber} - {link.linkedTicketSubject}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => deleteLinkMutation.mutate(link.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Merge Button */}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setMergeDialogOpen(true)}
          >
            <Merge className="h-4 w-4 mr-2" />
            Merge Ticket
          </Button>
        </div>
      </div>

      {/* Link Ticket Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Ticket</DialogTitle>
            <DialogDescription>
              Link another ticket to #{ticket.ticketNumber}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddLink} className="space-y-4">
            <div>
              <Label>Link Type</Label>
              <Select value={linkType} onValueChange={setLinkType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="related">Related</SelectItem>
                  <SelectItem value="blocks">Blocks</SelectItem>
                  <SelectItem value="blocked_by">Blocked By</SelectItem>
                  <SelectItem value="duplicate">Duplicate</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ticket Number</Label>
              <Input
                type="number"
                value={linkTicketNumber}
                onChange={(e) => setLinkTicketNumber(e.target.value)}
                placeholder="Enter ticket number"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setLinkDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!linkTicketNumber || createLinkMutation.isPending}
              >
                {createLinkMutation.isPending ? 'Linking...' : 'Link Ticket'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Merge Ticket Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Ticket</DialogTitle>
            <DialogDescription>
              Merge ticket #{ticket.ticketNumber} into another ticket. This will move all
              messages and close this ticket.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Target Ticket ID</Label>
              <Input
                value={mergeTargetTicketId}
                onChange={(e) => setMergeTargetTicketId(e.target.value)}
                placeholder="Enter target ticket ID"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMergeDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={!mergeTargetTicketId || mergeMutation.isPending}
                onClick={() => mergeMutation.mutate(mergeTargetTicketId)}
              >
                {mergeMutation.isPending ? 'Merging...' : 'Merge Ticket'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
